/**
 * Hook to configure filters based on available games
 */

import { useMemo } from 'react';

import { type CampaignTypes, useCampaigns, PRIZE_POP_CAMPAIGN_TYPE, TOURNAMENTS_CAMPAIGN_TYPE } from 'features/campaigns';
import { useJackpotsStore } from 'features/jackpots';
import { useSettings } from 'store/app/appStore';
import type IGameSettings from 'types/models/IGameSettings';
import { FILTERS as BASE_FILTERS } from '../constants/filterConfig';
import { SEARCH_CAMPAIGNS_OPTIONS, SEARCH_FILTERS } from '../constants/searchConstants';
import type { IFilterConfig } from '../types/filter.types';
import {
  createFilterLinesOptions,
  filterCampaignOptionsByAvailability,
  filterFeatureOptionsByAvailability,
  filterJackpotOptionsByAvailability,
  filterMaxWinOptionsByAvailability,
  filterPayoutOptionsByAvailability,
} from '../utils/filterOptionsHelpers';

export const useFilterConfig = (games: IGameSettings[]): IFilterConfig[] => {
  const { restrictions } = useSettings();
  const tournaments = useCampaigns(TOURNAMENTS_CAMPAIGN_TYPE);
  const prizePopCampaigns = useCampaigns(PRIZE_POP_CAMPAIGN_TYPE);
  // Jackpot stats load asynchronously (after sections). The Premium Link option
  // is derived from them, so recompute the options once jackpot data arrives.
  const hasJackpotData = useJackpotsStore((state) => state.hasData);

  const hasHideBuyBonusRestriction = restrictions.includes('hideBuyBonus');

  return useMemo(() => {
    // Define supported campaign types and derive which ones are currently visible
    const supportedCampaigns = [
      {
        id: SEARCH_CAMPAIGNS_OPTIONS.TOURNAMENTS,
        isVisible: tournaments.length > 0,
      },
      {
        id: SEARCH_CAMPAIGNS_OPTIONS.PRIZE_POP,
        isVisible: prizePopCampaigns.length > 0,
      },
    ];

    const visibleCampaignIds = new Set<CampaignTypes>(
      supportedCampaigns.filter((campaign) => campaign.isVisible).map((campaign) => campaign.id),
    );

    return BASE_FILTERS.map((filter) => {
      switch (filter.id) {
        case SEARCH_FILTERS.LINES:
          return {
            ...filter,
            options: createFilterLinesOptions(games),
          };
        case SEARCH_FILTERS.FEATURES:
          return {
            ...filter,
            options: filterFeatureOptionsByAvailability(games, filter.options, hasHideBuyBonusRestriction),
          };
        case SEARCH_FILTERS.MAX_WIN:
          return {
            ...filter,
            options: filterMaxWinOptionsByAvailability(games, filter.options),
          };
        case SEARCH_FILTERS.PAYOUT:
          return {
            ...filter,
            options: filterPayoutOptionsByAvailability(games, filter.options),
          };
        case SEARCH_FILTERS.JACKPOT:
          return {
            ...filter,
            options: filterJackpotOptionsByAvailability(games, filter.options),
          };
        case SEARCH_FILTERS.CAMPAIGNS:
          return filterCampaignOptionsByAvailability(filter, visibleCampaignIds);
        default:
          return filter;
      }
    }).filter((filter): filter is IFilterConfig => filter !== null);
  }, [games, hasHideBuyBonusRestriction, tournaments.length, prizePopCampaigns.length, hasJackpotData]);
};
